import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Check, Loader2, Plus, X } from "lucide-react";
import { LISTING_STATUS_LABELS } from "../listing/constants";

const HOA_PERIODS = ["Monthly", "Quarterly", "Annually"];

const FIELD_ROWS = [
  ["price", "List Price", "e.g. $450,000"], ["beds", "Bedrooms", "e.g. 3"], ["baths", "Bathrooms", "e.g. 2"], ["sqft", "Square Feet", "e.g. 1,800"],
  ["property_type", "Property Type", "e.g. Single Family"], ["lot_size", "Lot Size", "e.g. 0.25 acres"],
  ["year_built", "Year Built", "e.g. 1998"], ["stories", "Stories", "e.g. 2"], ["parking", "Parking", "e.g. 2-car garage"],
  ["heating_cooling", "Heating / Cooling", "e.g. Central HVAC"], ["mls_number", "MLS #", "e.g. TX1234567"], ["taxes", "Annual Taxes", "e.g. $4,200"],
];

function Field({ id, label, value, onChange, placeholder, disabled }) {
  return (
    <div>
      <Label htmlFor={id} className="text-xs text-slate-500">{label}</Label>
      <Input id={id} data-testid={`client-listing-${id}`} value={value || ""} placeholder={placeholder} disabled={disabled} onChange={(e) => onChange(e.target.value)} className="mt-1.5" />
    </div>
  );
}

export function ListingEditForm({ job, onSaved, readOnly }) {
  const delivery = job.delivery || {};
  const [address, setAddress] = useState({ street: job.address?.street || "", city: job.address?.city || "", state: job.address?.state || "", zip: job.address?.zip || "" });
  const [fields, setFields] = useState(() => Object.fromEntries(FIELD_ROWS.map(([key]) => [key, delivery[key] || ""])));
  const [listingStatus, setListingStatus] = useState(delivery.listing_status || "");
  const [hoaFee, setHoaFee] = useState(delivery.hoa_fee || "");
  const [hoaPeriod, setHoaPeriod] = useState(delivery.hoa_period || "");
  const [description, setDescription] = useState(delivery.website_description || "");
  const [highlights, setHighlights] = useState(delivery.highlights || []);
  const [newHighlight, setNewHighlight] = useState("");
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  const addHighlight = () => {
    const value = newHighlight.trim();
    if (!value || highlights.length >= 12) return;
    setHighlights([...highlights, value]);
    setNewHighlight("");
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/jobs/${job.id}/listing`, {
        address, ...fields, listing_status: listingStatus || null, hoa_fee: hoaFee, hoa_period: hoaPeriod || null,
        website_description: description, highlights,
      });
      onSaved(data);
      toast.success("Your property website has been updated", { duration: 5000 });
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 3000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save your changes");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="client-listing-form" className="space-y-8">
      <div>
        <h2 className="font-display text-xl">Address</h2>
        <div className="grid sm:grid-cols-2 gap-4 mt-4">
          <Field id="street" label="Street Address" value={address.street} onChange={(v) => setAddress({ ...address, street: v })} disabled={readOnly} />
          <Field id="city" label="City" value={address.city} onChange={(v) => setAddress({ ...address, city: v })} disabled={readOnly} />
          <Field id="state" label="State" value={address.state} onChange={(v) => setAddress({ ...address, state: v })} disabled={readOnly} />
          <Field id="zip" label="Zip" value={address.zip} onChange={(v) => setAddress({ ...address, zip: v })} disabled={readOnly} />
        </div>
      </div>

      <div>
        <h2 className="font-display text-xl">Listing Status</h2>
        <Select value={listingStatus} onValueChange={setListingStatus} disabled={readOnly}>
          <SelectTrigger data-testid="client-listing-status" className="mt-3 max-w-xs"><SelectValue placeholder="Select a status" /></SelectTrigger>
          <SelectContent>
            {Object.entries(LISTING_STATUS_LABELS).map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div>
        <h2 className="font-display text-xl">Property Facts</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
          {FIELD_ROWS.map(([key, label, placeholder]) => (
            <Field key={key} id={key} label={label} value={fields[key]} placeholder={placeholder} onChange={(v) => setFields({ ...fields, [key]: v })} disabled={readOnly} />
          ))}
          <div>
            <Label className="text-xs text-slate-500">HOA Fee</Label>
            <div className="flex gap-2 mt-1.5">
              <Input data-testid="client-listing-hoa_fee" value={hoaFee} placeholder="e.g. $150" disabled={readOnly} onChange={(e) => setHoaFee(e.target.value)} />
              <Select value={hoaPeriod} onValueChange={setHoaPeriod} disabled={readOnly}>
                <SelectTrigger data-testid="client-listing-hoa_period" className="w-28 shrink-0"><SelectValue placeholder="Period" /></SelectTrigger>
                <SelectContent>{HOA_PERIODS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      <div>
        <h2 className="font-display text-xl">Description</h2>
        <Textarea data-testid="client-listing-description" value={description} onChange={(e) => setDescription(e.target.value)} rows={5} placeholder="Tell buyers what makes this property special…" disabled={readOnly} className="mt-3" />
      </div>

      <div>
        <h2 className="font-display text-xl">Highlights</h2>
        <div className="flex flex-wrap gap-2 mt-3">
          {highlights.map((h, i) => (
            <span key={`${i}-${h}`} data-testid={`client-listing-highlight-${i}`} className="inline-flex items-center gap-1.5 rounded-full bg-[#F8F7F4] border border-[#E7E4DE] text-sm px-3 py-1.5">
              {h}
              {!readOnly && <button type="button" data-testid={`client-listing-highlight-remove-${i}`} onClick={() => setHighlights(highlights.filter((_, idx) => idx !== i))} className="text-slate-400 hover:text-red-500"><X className="h-3.5 w-3.5" /></button>}
            </span>
          ))}
        </div>
        <div className="flex gap-2 mt-3 max-w-md">
          <Input data-testid="client-listing-highlight-input" value={newHighlight} onChange={(e) => setNewHighlight(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addHighlight())} placeholder="e.g. Recently renovated" disabled={readOnly} />
          <Button type="button" data-testid="client-listing-highlight-add" variant="outline" onClick={addHighlight} disabled={readOnly}><Plus className="h-4 w-4" /></Button>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Button data-testid="client-listing-save" onClick={save} disabled={saving || readOnly} className="rounded-none bg-slate-900 hover:bg-slate-800 text-slate-50 px-8">
          {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving…</> : readOnly ? "Read-only" : "Save Changes"}
        </Button>
        {justSaved && <span data-testid="client-listing-saved" className="text-sm text-emerald-700 flex items-center gap-1.5"><Check className="h-4 w-4" />Saved</span>}
      </div>
    </div>
  );
}
